import { useEffect, useMemo, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '../../i18n';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import ServiceCard from '../../components/ServiceCard';
import { popUpIcon, testDriveIcon } from '../../assets/images/icons';
import './Servicios.scss';

gsap.registerPlugin(ScrollTrigger);

const Servicios = () => {
  const { t } = useLanguage();
  const reduceMotion = usePrefersReducedMotion();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const servicesRef = useRef([]);

  const services = useMemo(
    () => [
      { id: 'pop-ups', icon: popUpIcon, title: t('servicios.items.popUps') },
      { id: 'test-drive', icon: testDriveIcon, title: t('servicios.items.testDrive') },
    ],
    [t],
  );

  useEffect(() => {
    if (reduceMotion) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        subtitleRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: { trigger: subtitleRef.current, start: 'top 85%' },
        },
      );

      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2,
          scrollTrigger: { trigger: titleRef.current, start: 'top 80%' },
        },
      );

      gsap.fromTo(
        '.servicios__line',
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1.5,
          ease: 'power2.inOut',
          scrollTrigger: { trigger: '.servicios__line', start: 'top 80%' },
        },
      );

      gsap.fromTo(
        servicesRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: { trigger: '.servicios__grid', start: 'top 75%' },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [reduceMotion]);

  return (
    <section
      className="servicios servicios--home"
      id="servicios"
      ref={sectionRef}
      aria-labelledby="servicios-title"
    >
      <div className="container">
        <div className="servicios__header">
          <p className="servicios__subtitle" ref={subtitleRef}>
            {t('servicios.subtitle')}
          </p>
          <h2 id="servicios-title" className="servicios__title" ref={titleRef}>
            {t('servicios.title')}
          </h2>
          <div className="servicios__line" aria-hidden="true" />
        </div>

        <div className="servicios__grid servicios__grid--home">
          <div className="servicios__row servicios__row--home">
            {services.map((service, index) => (
              <ServiceCard
                key={service.id}
                ref={(el) => (servicesRef.current[index] = el)}
                icon={service.icon}
                title={service.title}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Servicios;
